import React from "react";
import { Mail } from "lucide-react";
import { JOB_FAIR_CONFIG } from "../../constants/jobFairIntro";

const JobFairIntro = ({ organizationName, branding, isWhiteLabelingEnable, onNext }) => {
  const light = isWhiteLabelingEnable;

  return (
    <div className={`min-h-screen w-screen flex flex-col overflow-y-auto relative ${light ? "bg-white" : "app-background"}`}>
      <a
        href="mailto:support@geniehire.ai?subject=Rozgar%20Mahakumbh%20Job%20Fair%20-%20Support"
        className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-10 inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium rounded-full px-3 py-1.5 border transition-colors ${
          light
            ? "text-[#1569c4] border-[#1569c4]/30 bg-white hover:bg-[#1569c4]/5"
            : "text-gray-200 border-white/20 hover:bg-white/10"
        }`}
      >
        <Mail className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Need help? </span>Support
      </a>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        {/*
          LEFT column — event branding. `contents` on mobile removes this
          wrapper from the box tree so its two children (header, info) become
          plain flex items of the row above and can be reordered independently
          around the photos; `md:flex md:flex-col md:w-1/2` restores it as a
          real half-width column on desktop, where order-none lets the header
          and info sit back in natural (DOM) order.
        */}
        <div className={`contents md:flex md:flex-col md:justify-center md:w-1/2 ${light ? "md:bg-[#EEF3FC]" : "md:bg-black/20"}`}>
          {/* Logo + title — first on mobile */}
          <div className={`order-1 md:order-none flex flex-col items-center text-center px-6 sm:px-10 lg:px-16 py-6 md:py-0 md:mb-4 ${light ? "bg-[#EEF3FC]" : "bg-black/20"} md:bg-transparent`}>
            <img
              src={branding?.eventLogo || JOB_FAIR_CONFIG.eventLogo}
              alt="Event logo"
              className="h-14 sm:h-16 object-contain"
            />
            <p className={`text-base sm:text-lg font-semibold mb-3 ${light ? "text-gray-900" : "text-white"}`}>
              {JOB_FAIR_CONFIG.eventWordmark}
              <span className={`block text-[10px] sm:text-xs font-normal tracking-wide ${light ? "text-gray-500" : "text-gray-400"}`}>
                {JOB_FAIR_CONFIG.eventWordmarkTagline}
              </span>
            </p>
            <h1 className={`text-lg sm:text-2xl lg:text-3xl font-bold leading-tight whitespace-nowrap ${light ? "text-gray-900" : "text-white"}`}>
              {JOB_FAIR_CONFIG.eventName}
            </h1>
            <p className={`text-xs sm:text-sm ${light ? "text-gray-500" : "text-gray-400"}`}>
              {JOB_FAIR_CONFIG.eventNameMarathi} · {JOB_FAIR_CONFIG.eventRegion} · {JOB_FAIR_CONFIG.eventDate}
            </p>
          </div>

          {/* Department / intro / org line — third on mobile */}
          <div className={`order-3 md:order-none px-6 sm:px-10 lg:px-16 pb-6 md:py-0 ${light ? "bg-[#EEF3FC]" : "bg-black/20"} md:bg-transparent`}>
            <p className={`text-[11px] uppercase tracking-widest font-semibold mt-2 ${light ? "text-gray-400" : "text-gray-500"}`}>
              An initiative by
            </p>
            <p className={`text-sm lg:text-base font-medium ${light ? "text-gray-700" : "text-gray-200"}`}>
              {JOB_FAIR_CONFIG.department}
            </p>
            <p className={`hidden lg:block text-xs mt-0.5 ${light ? "text-gray-400" : "text-gray-500"}`}>
              {JOB_FAIR_CONFIG.departmentMarathi}
            </p>

            <p className={`text-sm lg:text-base leading-relaxed mt-4 ${light ? "text-gray-600" : "text-gray-300"}`}>
              {JOB_FAIR_CONFIG.introText}
            </p>
            <p className={`hidden lg:block text-xs leading-relaxed mt-2 ${light ? "text-gray-400" : "text-gray-500"}`}>
              {JOB_FAIR_CONFIG.introTextMarathi}
            </p>

            {organizationName && (
              <p className={`text-sm mt-4 ${light ? "text-gray-600" : "text-gray-300"}`}>
                <span className="font-semibold">{organizationName}</span> is a participating employer
                at this job fair.
              </p>
            )}
          </div>
        </div>

        {/* RIGHT column — photos + CTA. Same `contents` pattern as the LEFT column. */}
        <div className="contents md:flex md:flex-col md:w-1/2 md:items-center md:justify-center">
          {/* Photos — second on mobile */}
          <div className="order-2 md:order-none flex flex-col items-center px-6 pt-8 md:pt-0 md:py-0">
            <div className="flex justify-center gap-8 sm:gap-14 mb-5">
              {JOB_FAIR_CONFIG.people.map((person, i) => (
                <div key={i} className="flex flex-col items-center">
                  <img
                    src={person.photo}
                    alt={person.name}
                    className={`w-24 h-24 sm:w-32 sm:h-32 lg:w-36 lg:h-36 rounded-full object-cover border mb-2 ${light ? "border-gray-200" : "border-white/20"}`}
                  />
                  <p className={`font-semibold text-xs sm:text-sm ${light ? "text-gray-900" : "text-white"}`}>
                    {person.name}
                  </p>
                  <p className={`text-[10px] sm:text-xs leading-snug max-w-[160px] sm:max-w-[220px] ${light ? "text-gray-500" : "text-gray-400"}`}>
                    {person.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Info + CTA — fourth (last) on mobile */}
          <div className="order-4 md:order-none flex flex-col items-center px-6 pb-8 md:pb-0">
            <p className={`text-sm sm:text-base text-center ${light ? "text-gray-700" : "text-gray-200"}`}>
              {JOB_FAIR_CONFIG.thankYouText}
            </p>
            <p className={`text-sm sm:text-base text-center mb-6 ${light ? "text-gray-700" : "text-gray-200"}`}>
              {JOB_FAIR_CONFIG.bestOfLuckText}
            </p>

            <button
              onClick={onNext}
              className={`w-full sm:w-auto py-3 px-14 rounded-xl font-semibold text-sm tracking-wide transition-all duration-150 shadow-md active:scale-[0.98] ${
                light
                  ? "bg-[#1569c4] hover:bg-[#125eaf] text-white"
                  : "bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90"
              }`}
            >
              Processed Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobFairIntro;
